import { Chip } from '@mui/material';
import React from 'react';

/**
 * Shared formatters and utilities for MUI DataGrid columns
 */

/**
 * MUI Chip color types
 */
type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type ChipVariant = 'filled' | 'outlined';

/**
 * Centralized color mappings for chip display across all entities
 * Organized by domain and field type for maintainability
 */
const CHIP_COLOR_MAPS: Record<string, Record<string, ChipColor>> = {
  // Quotation domain
  quotationStatus: {
    VALID: 'success',
    DRAFT: 'default',
    NOT_VALID: 'error',
    SERVICE_REQUESTED: 'info',
  },
  paymentStatus: {
    SUCCESS: 'success',
    REQUESTED: 'info',
    INITIATED: 'primary',
    REFUSED: 'warning',
    ERROR: 'error',
  },
  quotationRequestStatus: {
    DRAFT: 'default',
    PENDING_OFFERS: 'info',
    PENDING_OFFER_SELECTION: 'warning',
    OFFER_SELECTED: 'success',
    MANUAL_QUOTATION_REQUESTED: 'secondary',
  },
  quotationProcess: {
    SELF_CARE: 'primary',
    CRM: 'secondary',
  },
  
  // Service domain
  serviceType: {
    TRANSPORT: 'primary',
    WAREHOUSING: 'secondary',
    CUSTOMS: 'info',
    INSURANCE: 'warning',
  },
  serviceSubtype: {
    ROAD_TRANSPORT: 'primary',
    FTL_LTL: 'info',
    MILKUN: 'secondary',
    EXPRESS: 'error',
    COURRIER: 'warning',
  },
  
  // Notifications domain
  notificationType: {
    UPDATES: 'primary',
    EVENTS: 'secondary',
    ACL: 'info',
  },
  resourceType: {
    // Default behavior will handle unknown values
  },
  subscriptionStatus: {
    ACTIVE: 'success',
    DISABLED: 'error',
  },
  consumerStatus: {
    ACTIVE: 'success',
    DISABLED: 'error',
  },
  
  // Payments domain
  merchantStatus: {
    LIVE: 'success',
    NEW: 'info',
    BLOCKED: 'error',
    DISABLED: 'warning',
  },
  bankAccountStatus: {
    LIVE: 'success',
    NEW: 'info',
    BLOCKED: 'error',
  },
  quotationPaymentStatus: {
    SUCCESS: 'success',
    INITIATED: 'info',
    REQUESTED: 'default',
    REFUSED: 'error',
    ERROR: 'error',
  },
  
  changeRequestStatus: {
    ACCEPTED: 'success',
    PENDING: 'warning',
    REJECTED: 'error',
  },

  // Events domain
  domain: {
    BUSINESS: 'primary',
    ISSUE: 'error',
  },
};

/**
 * Get MUI Chip color for a given field and value
 * @param fieldName - The field name (e.g., 'quotationStatus', 'serviceType')
 * @param value - The actual value from the field
 * @param defaultColor - Fallback color if no mapping found
 * @returns ChipColor
 */
export const getChipColor = (
  fieldName: string,
  value: string,
  defaultColor: ChipColor = 'default'
): ChipColor => {
  return CHIP_COLOR_MAPS[fieldName]?.[value] || defaultColor;
};

/**
 * Render a value as a MUI Chip with appropriate color based on field type
 * @param fieldName - The field name to determine color mapping
 * @param value - The value to display
 * @param variant - Chip variant ('filled' | 'outlined')
 * @param defaultColor - Fallback color if no mapping found
 * @returns React.ReactElement or string
 */
export const renderChip = (
  fieldName: string,
  value: string | null | undefined,
  variant: ChipVariant = 'filled',
  defaultColor: ChipColor = 'default'
): React.ReactElement | string => {
  if (!value) return '-';
  
  return React.createElement(Chip, {
    label: value,
    color: getChipColor(fieldName, value, defaultColor),
    size: 'small',
    variant,
  });
};

/**
 * Format a date-time string to a localized string with user's timezone
 * @param dateTime - ISO date string
 * @param locale - Browser locale (defaults to user's browser locale)
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateTime: string | null | undefined, locale?: string): string => {
  if (!dateTime) return '-';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    
    const userLocale = locale || navigator.language;
    const dateStr = date.toLocaleDateString(userLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString(userLocale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    
    return `${dateStr} ${timeStr}`;
  } catch {
    return '-';
  }
};

/**
 * Format a date-time string to a date only string with user's timezone
 * @param dateTime - ISO date string
 * @param locale - Browser locale (defaults to user's browser locale)
 * @returns Formatted date string
 */
export const formatDate = (dateTime: string | null | undefined, locale?: string): string => {
  if (!dateTime) return '-';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    
    const userLocale = locale || navigator.language;
    return date.toLocaleDateString(userLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * Format a currency value with its unit code
 */
export const formatCurrency = (value: number | null | undefined, currencyCode: string | null | undefined): string => {
  if (value === null || value === undefined || !currencyCode) return '-';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value} ${currencyCode}`;
  }
};

/**
 * Format a value with its unit code
 */
export const formatValueWithUnit = (
  value: number | null | undefined,
  unitCode: string | null | undefined
): string => {
  if (value === null || value === undefined) return '-';
  if (!unitCode) return `${value}`;
  return `${value} ${unitCode}`;
};

/**
 * Extract unique values from an array
 */
export const getUniqueValues = <T>(array: T[] | null | undefined): T[] => {
  if (!array || !Array.isArray(array)) return [];
  return [...new Set(array)];
};

/**
 * Join array of strings with a delimiter, filtering out empty values
 */
export const joinArray = (
  array: (string | null | undefined)[] | null | undefined,
  delimiter: string = ', '
): string => {
  if (!array || !Array.isArray(array)) return '-';
  const filtered = array.filter((item): item is string => Boolean(item));
  return filtered.length > 0 ? filtered.join(delimiter) : '-';
};

/**
 * Join unique values from an array
 */
export const joinUniqueValues = (
  array: (string | null | undefined)[] | null | undefined,
  delimiter: string = ', '
): string => {
  if (!array || !Array.isArray(array)) return '-';
  const unique = getUniqueValues(array.filter((item): item is string => Boolean(item)));
  return unique.length > 0 ? unique.join(delimiter) : '-';
};

/**
 * Get a display name from an entity with fallback options
 */
export const getEntityDisplayName = (entity: {
  commercialName?: string | null;
  legalEntityName?: string | null;
  name?: string | null;
} | null | undefined): string => {
  if (!entity) return '-';
  return entity.commercialName || entity.legalEntityName || entity.name || '-';
};

/**
 * Format address as "City, PostalCode"
 */
export const formatAddressShort = (address: {
  addressLocality?: string | null;
  postalCode?: string | null;
} | null | undefined): string => {
  if (!address) return '-';
  const parts = [address.addressLocality, address.postalCode].filter(Boolean);
  if (parts.length === 0) return '-';
  return parts.join(', ');
};

/**
 * Format a location from nested structure
 */
export const formatLocation = (location: {
  location?: {
    address?: {
      addressLocality?: string | null;
      postalCode?: string | null;
    } | null;
  } | null;
} | null | undefined): string => {
  if (!location?.location?.address) return '-';
  return formatAddressShort(location.location.address);
};

/**
 * Sum numeric values from an array of objects
 */
export const sumValues = <T>(
  array: T[] | null | undefined,
  getValue: (item: T) => number | null | undefined
): number => {
  if (!array || !Array.isArray(array)) return 0;
  return array.reduce((sum, item) => {
    const value = getValue(item);
    return sum + (value || 0);
  }, 0);
};

/**
 * Format external references as "type: reference"
 */
export const formatExternalReferences = (
  references: Array<{
    externalReferenceType?: string | null;
    reference?: string | null;
  }> | null | undefined,
  delimiter: string = ', '
): string => {
  if (!references || !Array.isArray(references)) return '-';
  const formatted = references
    .filter(ref => ref.externalReferenceType && ref.reference)
    .map(ref => `${ref.externalReferenceType}: ${ref.reference}`);
  return formatted.length > 0 ? formatted.join(delimiter) : '-';
};

/**
 * Format special characteristics as "type: value"
 */
export const formatSpecialCharacteristics = (
  characteristics: Array<{
    specialCharacteristicType?: string | null;
    characteristicReference?: string | null;
    stringValue?: string | null;
    numericalValue?: { value?: number; unit?: string } | null;
  }> | null | undefined,
  delimiter: string = ', '
): string => {
  if (!characteristics || !Array.isArray(characteristics)) return '-';
  const formatted = characteristics
    .filter(char => char.specialCharacteristicType)
    .map(char => {
      const type = char.specialCharacteristicType;
      let value = char.stringValue || char.characteristicReference;
      
      if (!value && char.numericalValue?.value !== undefined) {
        const numValue = char.numericalValue.value;
        const unit = char.numericalValue.unit ? ` ${char.numericalValue.unit}` : '';
        value = `${numValue}${unit}`;
      }
      
      return value ? `${type}: ${value}` : type;
    });
  return formatted.length > 0 ? formatted.join(delimiter) : '-';
};

/**
 * Filter external references by type pattern
 */
export const filterExternalReferencesByType = (
  references: Array<{
    externalReferenceType?: string | null;
    reference?: string | null;
  }> | null | undefined,
  typePattern: string
): Array<string> => {
  if (!references || !Array.isArray(references)) return [];
  return references
    .filter(ref => ref.externalReferenceType?.includes(typePattern) && ref.reference)
    .map(ref => ref.reference as string);
};

/**
 * Get tracking IDs from external references
 */
export const getTrackingIds = (
  externalReferences: Array<{
    externalReferenceType?: string | null;
    reference?: string | null;
  }> | null | undefined
): string => {
  return joinArray(filterExternalReferencesByType(externalReferences, '_TRACKING_ID'));
};

/**
 * Get tracking URLs from external references
 */
export const getTrackingUrls = (
  externalReferences: Array<{
    externalReferenceType?: string | null;
    reference?: string | null;
  }> | null | undefined
): string => {
  return joinArray(filterExternalReferencesByType(externalReferences, '_TRACKING_URL'));
};

/**
 * Get LOB quotation IDs from external references
 */
export const getLobQuotationIds = (
  externalReferences: Array<{
    externalReferenceType?: string | null;
    reference?: string | null;
  }> | null | undefined
): string => {
  return joinArray(filterExternalReferencesByType(externalReferences, '_QUOTATION_ID'));
};

/**
 * Render boolean as Yes/No chip (returns React element)
 */
export const renderBooleanChip = (value: boolean | null | undefined): React.ReactElement => {
  if (value === null || value === undefined) {
    return React.createElement(Chip, {
      label: '-',
      size: 'small',
      color: 'default',
      variant: 'outlined',
    });
  }

  if (value === true) {
    return React.createElement(Chip, {
      label: 'Yes',
      size: 'small',
      color: 'success',
      variant: 'filled',
    });
  }

  return React.createElement(Chip, {
    label: 'No',
    size: 'small',
    color: 'error',
    variant: 'filled',
  });
};

/**
 * Calculate duration between start and end dates with human-readable format
 */
export const calculateDuration = (startDate: string | null | undefined, endDate: string | null | undefined): string => {
  if (!startDate || !endDate) return '-';
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '-';
    
    const durationMs = end.getTime() - start.getTime();
    
    // Return '-' for negative durations
    if (durationMs < 0) return '-';
    
    const durationSeconds = Math.floor(durationMs / 1000);
    
    if (isNaN(durationSeconds)) return '-';
    
    // Calculate days, hours, minutes, seconds
    const days = Math.floor(durationSeconds / 86400);
    const hours = Math.floor((durationSeconds % 86400) / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const seconds = durationSeconds % 60;
    
    // Build human-readable string
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && parts.length === 0) parts.push(`${seconds}s`); // Only show seconds if no larger units
    
    return parts.length > 0 ? parts.join(' ') : '0s';
  } catch {
    return '-';
  }
};

/**
 * Get HTTP status code chip color
 * @param status - HTTP status code
 * @returns ChipColor for the status code
 */
export const getHttpStatusChipColor = (status: number): ChipColor => {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'error';
  return 'default';
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis or original text
 */
export const truncateText = (text: string | null | undefined, maxLength: number = 50): string => {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Get notification count from JSON payload
 * @param payload - JSON string containing notification(s)
 * @returns Number of notifications in the payload
 */
export const getNotificationCount = (payload: string | null | undefined): number => {
  if (!payload) return 0;
  try {
    const parsed = JSON.parse(payload);
    const notifications = Array.isArray(parsed) ? parsed : [parsed];
    return notifications.length;
  } catch {
    return 0;
  }
};
